/** Polygons from pasted or uploaded GeoJSON or KML, every polygon and hole kept (design §4.5). */
import { openRing, polygonsFromGeoJson, type DrawnPolygon, type Ring } from './geometry';

export type ImportResult = { ok: true; polygons: DrawnPolygon[] } | { ok: false; reason: 'no_polygon' | 'invalid' | 'out_of_range' };

const inRange = ([lon, lat]: Ring[number]) => lon >= -180 && lon <= 180 && lat >= -90 && lat <= 90;

/** A projected CRS (S-JTSK, UTM) reads as numbers far outside longitude and latitude. */
function found(polygons: DrawnPolygon[]): ImportResult {
  if (!polygons.length) return { ok: false, reason: 'no_polygon' };
  const rings = polygons.flatMap((p) => [p.shell, ...p.holes]);
  return rings.every((r) => r.every(inRange)) ? { ok: true, polygons } : { ok: false, reason: 'out_of_range' };
}

function kmlRing(el: Element | undefined): Ring | null {
  const text = el?.getElementsByTagNameNS('*', 'coordinates')[0]?.textContent ?? '';
  const tuples = text
    .trim()
    .split(/\s+/)
    .map((t) => t.split(','))
    .filter((parts) => parts.every((s) => s !== ''))
    .map((parts) => parts.map(Number));
  return openRing(tuples);
}

function fromKml(text: string): ImportResult {
  const doc = new DOMParser().parseFromString(text, 'text/xml');
  if (doc.getElementsByTagName('parsererror').length) return { ok: false, reason: 'invalid' };
  const polygons: DrawnPolygon[] = [];
  for (const poly of Array.from(doc.getElementsByTagNameNS('*', 'Polygon'))) {
    const shell = kmlRing(poly.getElementsByTagNameNS('*', 'outerBoundaryIs')[0]);
    if (!shell) continue;
    const holes = Array.from(poly.getElementsByTagNameNS('*', 'innerBoundaryIs'))
      .map(kmlRing)
      .filter((r): r is Ring => r !== null);
    polygons.push({ shell, holes });
  }
  return found(polygons);
}

export function importPolygons(text: string): ImportResult {
  const trimmed = text.trim();
  if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
    try {
      return found(polygonsFromGeoJson(JSON.parse(trimmed)));
    } catch {
      return { ok: false, reason: 'invalid' };
    }
  }
  if (trimmed.startsWith('<')) return fromKml(trimmed);
  return { ok: false, reason: 'invalid' };
}
